import { useState, useRef, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { debounce } from 'lodash';
import { 
  isHebrewText, 
  hebrewPhoneticNormalize, 
  areHebrewWordsPhoneticallySimilar 
} from './hebrewPhoneticUtils';

// Levenshtein distance implementation to measure string similarity
const levenshteinDistance = (str1, str2) => {
  const track = Array(str2.length + 1).fill(null).map(() => 
    Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }
  
  return track[str2.length][str1.length];
};

// Format helper functions
const formatTableName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatColumnName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Custom hook for index search functionality
const useIndexSearch = (options = {}) => {
  const {
    minChars = 3,
    maxResults = 10,
    cacheTTL = 15 * 60 * 1000, // 15 minutes in milliseconds
    cachePrefix = 'indexSearch',
    shouldSortResults = true,
    allowedTables = null,
    debounceTime = 300
  } = options;

  const [indexCache, setIndexCache] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const allowedTablesRef = useRef(allowedTables);
  const commonWords = new Set([
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i', 'it', 'for',
    'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at', 'this', 'but', 'his',
    'by', 'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my',
    'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out', 'if',
    'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like',
    'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year', 'your',
    'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look',
    'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use', 'two',
    'how', 'our', 'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because',
    'any', 'these', 'give', 'day', 'most', 'us', 'compare'
  ]);

  // Update ref when allowedTables changes
  useEffect(() => {
    if (allowedTables && JSON.stringify(allowedTables) !== JSON.stringify(allowedTablesRef.current)) {
      allowedTablesRef.current = allowedTables;
      // Clear the cache when allowedTables changes
      setIndexCache(null);
    }
  }, [allowedTables]);

  // Cache management functions
  const saveToCache = useCallback((key, data) => {
    try {
      const cacheKey = `${cachePrefix}_${key}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_expiry`, String(Date.now() + cacheTTL));
    } catch (error) {
    }
  }, [cachePrefix, cacheTTL]);

  const getFromCache = useCallback((key) => {
    try {
      const cacheKey = `${cachePrefix}_${key}`;
      const expiryTime = parseInt(localStorage.getItem(`${cacheKey}_expiry`) || '0', 10);
      if (Date.now() > expiryTime) {
        return null; // Cache expired
      }
      
      const cachedData = localStorage.getItem(cacheKey);
      return cachedData ? JSON.parse(cachedData) : null;
    } catch (error) {
      return null;
    }
  }, [cachePrefix]);

  // Fetch all indexed columns
  const fetchIndexedColumns = useCallback(async (useCache = true) => {
    if (!allowedTablesRef.current) {
      return [];
    }

    const cacheKey = 'indexedColumns';
    try {
      setLoading(true);
      setError(null);
      
      // Try to get from cache first if useCache is true
      if (useCache) {
        const cachedColumns = getFromCache(cacheKey);
        if (cachedColumns) {
          return cachedColumns;
        }
      }
      
      // First get tables configuration
      const configDoc = await getDoc(doc(db, 'appConfiguration', 'tables'));
      if (!configDoc.exists()) {
        return [];
      }

      const docData = configDoc.data();
      const tablesData = Object.entries(docData)
        .filter(([_, value]) => 
          value && 
          typeof value === 'object' && 
          value.schema && 
          Array.isArray(value.schema) &&
          value.id && // Ensure table has an ID
          allowedTablesRef.current && // Check if allowedTables exists
          allowedTablesRef.current[value.id] // Check if table is allowed
        )
        .map(([name, tableData]) => ({
          id: tableData.id || name,
          name: name,
          displayName: formatTableName(name),
          schema: (tableData.schema || []).filter(field => 
            field && 
            field.name &&
            !field.name.toLowerCase().includes('created') &&
            !field.name.toLowerCase().includes('modified') &&
            !field.name.toLowerCase().includes('timestamp') &&
            field.mode !== 'REPEATED'
          )
        }));

      // Now get indexed columns from index collection
      const indexesSnapshot = await getDocs(collection(db, 'index'));
      const indexMap = new Map();
      
      indexesSnapshot.forEach(doc => {
        const docId = doc.id;
        const data = doc.data();
        
        // Use tableId and columnName from the document data
        if (data && data.tableId && data.columnName) {
          // Find the matching table by its ID
          const matchingTable = tablesData.find(table => table.id === data.tableId);
          
          if (matchingTable) {
            // Find the matching column in the schema
            const columnSchema = matchingTable.schema.find(col => col.name === data.columnName);
            if (columnSchema) {
              const key = `${matchingTable.id}_${data.columnName}`;
              indexMap.set(key, {
                tableId: matchingTable.id,
                tableName: matchingTable.name,
                tableDisplayName: matchingTable.displayName,
                columnName: data.columnName,
                columnDisplayName: formatColumnName(data.columnName),
                type: columnSchema.type || 'STRING'
              });
            }
          }
        }
      });
      
      const columnsArray = Array.from(indexMap.values());
      
      // Save to cache
      saveToCache(cacheKey, columnsArray);
      return columnsArray;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getFromCache, saveToCache]);

  // Get cached index data or fetch it
  const getIndexCache = useCallback(async () => {
    if (indexCache) return indexCache;
    if (!allowedTablesRef.current) {
      return new Map();
    }

    setLoading(true);
    try {
      const indexCollection = collection(db, 'index');
      const snapshot = await getDocs(indexCollection);
      const cache = new Map();
      const multiChunkIndexes = new Map();
      const chunkDocuments = new Map();
      
      // First pass: identify main documents and chunks
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Skip if no tableId
        if (!data.tableId) return;
        
        // Skip if table is not allowed
        if (!allowedTablesRef.current[data.tableId]) {
          return;
        }
        
        // Format table name
        const tableName = data.tableId.split('.').slice(-1)[0]
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        // Check if this is a chunk document
        if (data.isChunk && data.parentDocId) {
          if (!chunkDocuments.has(data.parentDocId)) {
            chunkDocuments.set(data.parentDocId, []);
          }
          chunkDocuments.get(data.parentDocId).push({
            docId: doc.id,
            chunkIndex: data.chunkIndex,
            values: data.values || []
          });
          return;
        }
        
        // Check if this is a multi-chunk main document
        if (data.isMultiChunk) {
          multiChunkIndexes.set(doc.id, {
            tableId: data.tableId,
            columnName: data.columnName,
            tableName,
            chunks: data.chunks,
            values: [] // Will be filled with values from chunks
          });
          return;
        }
        
        // If this is a regular document with values, add it directly
        if (data.values && Array.isArray(data.values)) {
          cache.set(doc.id, {
            values: data.values,
            tableId: data.tableId,
            columnName: data.columnName,
            tableName
          });
        }
      });
      
      // Second pass: merge chunks for multi-chunk indexes
      multiChunkIndexes.forEach((indexData, docId) => {
        // Get chunks for this index
        const chunks = chunkDocuments.get(docId) || [];
        
        // Sort chunks by index
        chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
        
        // Merge values from all chunks
        let allValues = [];
        chunks.forEach(chunk => {
          if (chunk.values && Array.isArray(chunk.values)) {
            allValues = [...allValues, ...chunk.values];
          }
        });
        
        // Add to cache with merged values
        if (allValues.length > 0) {
          indexData.values = allValues;
          cache.set(docId, indexData);
        }
      });
      
      setIndexCache(cache);
      return cache;
    } catch (err) {
      setError(err);
      return new Map();
    } finally {
      setLoading(false);
    }
  }, [indexCache]);

  // Fetch values for a specific column
  const fetchColumnValues = useCallback(async (column) => {
    if (!column || !column.tableId || !column.columnName) {
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const safeTableId = column.tableId.replace(/\./g, '_');
      const baseDocId = `${safeTableId}_${column.columnName}`;
      const mainDocRef = doc(db, 'index', baseDocId);
      const mainDocSnap = await getDoc(mainDocRef);
      
      if (mainDocSnap.exists()) {
        const data = mainDocSnap.data();
        
        // If this is a multi-chunk index
        if (data.isMultiChunk && data.chunks > 0) {
          
          // Create array to hold all values from all chunks
          let allValues = [];
          
          // Fetch all chunk documents
          for (let i = 1; i <= data.chunks; i++) {
            const chunkDocRef = doc(db, 'index', `${baseDocId}_chunk_${i}`);
            const chunkSnap = await getDoc(chunkDocRef);
            
            if (chunkSnap.exists()) {
              const chunkData = chunkSnap.data();
              if (chunkData && Array.isArray(chunkData.values)) {
                allValues = [...allValues, ...chunkData.values];
              }
            }
          }
          
          // Process values
          const uniqueValues = [...new Set(allValues.map(item => item.value))];
          return uniqueValues.map(value => ({ value }));
        } else if (data && Array.isArray(data.values)) {
          // Handle single-document case
          const uniqueValues = [...new Set(data.values.map(item => item.value))];
          return uniqueValues.map(value => ({ value }));
        }
      }
      
      return [];
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Search for values across all indexes
  const searchValues = useCallback(async (searchTerm, options = {}) => {
    const {
      previousWord = '',
      currentWord = searchTerm,
      currentWordStart = 0,
      currentWordEnd = currentWord.length,
      combinedWords = previousWord ? `${previousWord} ${currentWord}` : currentWord,
      searchColumnId = null, // If provided, only search this specific column
      exactMatchOnly = false, // If true, skip fuzzy matching
      skipQuotedCheck = false, // If true, skip checking for quoted values in input
      inputValue = '',  // Full input for quoted check
      lastSelectedPosition = { start: -1, end: -1, text: '' },
      fullInputSearch = null, // Full input for multi-word search
    } = options;

    // Early return conditions
    if (!currentWord || 
        (previousWord ? currentWord.length < 2 : currentWord.length < minChars)) {
      return { results: [], matchType: 'none' };
    }
    
    // Check if current position matches last selected position
    if (lastSelectedPosition.start !== -1 &&
        currentWordStart === lastSelectedPosition.start && 
        currentWordEnd === lastSelectedPosition.end &&
        currentWord === lastSelectedPosition.text) {
      return { results: [], matchType: 'lastSelected' };
    }
    
    // Skip if the search term is in the last selected text
    if (lastSelectedPosition.text && 
        lastSelectedPosition.text.toLowerCase().includes(currentWord.toLowerCase())) {
      return { results: [], matchType: 'withinLastSelected' };
    }

    // Check if value is within quotes (for normal search mode)
    if (!skipQuotedCheck && inputValue) {
      // Find all quoted segments in the entire input
      const quotedSegments = [];
      const quoteRegex = /"([^"]*)"/g;
      let match;
      while ((match = quoteRegex.exec(inputValue)) !== null) {
        quotedSegments.push({
          start: match.index,
          end: match.index + match[0].length,
          value: match[1]
        });
      }
      
      // Check if current word position overlaps with any quoted segment
      const isWithinQuotes = quotedSegments.some(segment => 
        (currentWordStart >= segment.start && currentWordStart < segment.end) || 
        (currentWordEnd > segment.start && currentWordEnd <= segment.end) ||
        (currentWordStart <= segment.start && currentWordEnd >= segment.end)
      );
      
      // If the current word is within quotes, don't show suggestions
      if (isWithinQuotes) {
        return { results: [], matchType: 'withinQuotes' };
      }
    }

    const cache = await getIndexCache();
    const results = [];
    const uniqueValues = new Map(); // Use Map instead of Set to store value -> valueContext mappings
    const wordOrderMatches = new Map(); // Track word-order matches separately
    const maxWordOrderMatches = 5; // Prioritize top 5 word-order matches

    // Create search variations
    const currentWordLower = currentWord.toLowerCase();
    const previousWordLower = previousWord ? previousWord.toLowerCase() : '';
    const combinedWordsLower = combinedWords.toLowerCase();
    
    // Check if we're doing a multi-word search
    const fullInputLower = fullInputSearch ? fullInputSearch.toLowerCase() : '';
    const isMultiWordSearch = (currentWordLower.includes(' ') || 
                              (previousWordLower && combinedWordsLower.includes(' ')) ||
                              (fullInputLower && fullInputLower.includes(' ')));
    
    // Use full input for search words if available
    let searchWords = [];
    if (fullInputLower && fullInputLower.includes(' ')) {
      searchWords = fullInputLower.split(/\s+/).filter(w => w.length > 0);
    } else if (isMultiWordSearch) {
      searchWords = (previousWordLower ? combinedWordsLower : currentWordLower).split(/\s+/).filter(w => w.length > 0);
    }

    // Check if current word starts with 'ל' and create alternative strings
    const hasLPrefix = currentWordLower.startsWith('ל');
    const currentWithoutL = hasLPrefix ? currentWordLower.slice(1) : currentWordLower;

    // Determine search strategy based on word lengths
    const shouldSearchCurrentWord = !previousWordLower && currentWordLower.length >= minChars;
    const shouldSearchCombined = previousWordLower && currentWordLower.length >= 2;
    const shouldIncludeCurrentWord = previousWordLower && currentWordLower.length >= minChars;

    // Check if the search term is Hebrew
    const isSearchHebrew = isHebrewText(currentWord);
    const normalizedSearchTerm = isSearchHebrew ? hebrewPhoneticNormalize(currentWord) : null;

    for (const [docId, data] of cache) {
      const tableId = data.tableId;

      // Skip if table is not in allowed tables
      if (!allowedTablesRef.current || !allowedTablesRef.current[tableId]) {
        continue;
      }
      
      // If searchColumnId is provided, only search this specific column
      if (searchColumnId && `${data.tableId}_${data.columnName}` !== searchColumnId) {
        continue;
      }
          
      data.values.filter(item => {
        if (!item.value) return false;
      
        const itemLower = item.value.toLowerCase();
        
        // For multi-word searches, check word-order match FIRST
        if (isMultiWordSearch && searchWords.length > 1 && wordOrderMatches.size < maxWordOrderMatches) {
          const itemWords = itemLower.split(/\s+/).filter(w => w.length > 0);
          const hasAllWords = searchWords.every(sw => 
            itemWords.some(iw => iw === sw || iw.includes(sw))
          );
          
          if (hasAllWords && !wordOrderMatches.has(itemLower)) {
            const contextEntry = {
              value: item.value,
              tableName: data.tableName,
              tableId: data.tableId,
              columnName: data.columnName,
              isExactMatch: true,
              matchType: 'word-order'
            };
            
            wordOrderMatches.set(itemLower, {
              value: item.value,
              originalSearch: currentWord,
              contexts: [contextEntry],
              isExactMatch: true,
              matchType: 'word-order'
            });
            return false; // Skip other checks for this item
          }
        }
        
        // Stop checking if we have enough matches
        if (wordOrderMatches.size >= maxWordOrderMatches && uniqueValues.size >= maxResults) {
          return false;
        }
        
        const isCombinedExact = previousWordLower && itemLower.includes(combinedWordsLower);
        if (uniqueValues.size >= maxResults && !uniqueValues.has(itemLower) && !isCombinedExact) return false;
                
        // Check for exact substring matching
        let isExactMatch = 
          (shouldSearchCombined && itemLower.includes(combinedWordsLower)) ||
          ((shouldSearchCurrentWord || shouldIncludeCurrentWord) && 
            (itemLower.includes(currentWordLower) || 
            (hasLPrefix && itemLower.includes(currentWithoutL))));
        
        
        // If exact match found, add to results
        if (isExactMatch) {
          // Build the context entry for this value with exact match flag
          const contextEntry = {
            value: item.value,
            tableName: data.tableName,
            tableId: tableId,
            columnName: data.columnName,
            isExactMatch: true,
            matchType: 'exact'
          };
          
          // Add to results with original logic
          if (uniqueValues.has(itemLower)) {
            const existing = uniqueValues.get(itemLower);
            existing.contexts.push(contextEntry);
          } else {
            uniqueValues.set(itemLower, {
              value: item.value,
              originalSearch: currentWord,
              contexts: [contextEntry],
              isExactMatch: true,
              matchType: 'exact'
            });
          }
          
          return false;
        }

        // Check for Hebrew phonetic matching
        if (isSearchHebrew && isHebrewText(item.value) && normalizedSearchTerm) {
          if (areHebrewWordsPhoneticallySimilar(currentWord, item.value)) {
            const contextEntry = {
              value: item.value,
              tableName: data.tableName,
              tableId: tableId,
              columnName: data.columnName,
              isExactMatch: false,
              matchType: 'phonetic'
            };
            
            if (uniqueValues.has(itemLower)) {
              const existing = uniqueValues.get(itemLower);
              // Don't add if we already have this as exact match
              if (existing.matchType !== 'exact') {
                existing.contexts.push(contextEntry);
                existing.matchType = 'phonetic';
              }
            } else {
              uniqueValues.set(itemLower, {
                value: item.value,
                originalSearch: currentWord,
                contexts: [contextEntry],
                isExactMatch: false,
                matchType: 'phonetic'
              });
            }
            return false;
          }
        }
        
        // If requested exact matches only, skip fuzzy matching
        if (exactMatchOnly) {
          return false;
        }
        
        // Try fuzzy matching for longer words
        if ((currentWordLower.length >= 3) && 
        ((shouldSearchCurrentWord || shouldIncludeCurrentWord))) {

        // Use the term without ל prefix for fuzzy matching if applicable
        const searchTermForFuzzy = hasLPrefix ? currentWithoutL : currentWordLower;

        // Calculate normalized threshold based on word length (20% of length, min 1)
        const threshold = Math.max(1, Math.floor(searchTermForFuzzy.length * 0.2));

        // Get distance between search term and this value
        let distance = Infinity;
        if (itemLower.includes(' ')) {
        // For multi-word values, check each word individually
        const words = itemLower.split(' ');
        for (const word of words) {
          if (word.length >= 2) { // Only check words with at least 2 chars
            const wordDistance = levenshteinDistance(
              searchTermForFuzzy,
              word.substring(0, Math.min(word.length, searchTermForFuzzy.length + 3))
            );
            distance = Math.min(distance, wordDistance);
          }
        }
        } else {
        // For single-word values, check as before
        distance = levenshteinDistance(
          searchTermForFuzzy,
          itemLower.substring(0, Math.min(itemLower.length, searchTermForFuzzy.length + 3))
        );
        }
          
          // For shorter words, be more strict
          const adjustedThreshold = currentWordLower.length <= 4 ? 1 : threshold;
          
          // If within threshold, it's a fuzzy match
          if (distance <= adjustedThreshold) {
            const contextEntry = {
              value: item.value,
              tableName: data.tableName,
              tableId: tableId,
              columnName: data.columnName,
              isExactMatch: false,
              matchType: 'fuzzy',
              distance: distance
            };
            
            if (uniqueValues.has(itemLower)) {
              const existing = uniqueValues.get(itemLower);
              // Don't add if we already have this as exact or phonetic match
              if (!['exact', 'phonetic'].includes(existing.matchType)) {
                existing.contexts.push(contextEntry);
                existing.distance = distance;
              }
            } else {
              uniqueValues.set(itemLower, {
                value: item.value,
                originalSearch: currentWord,
                contexts: [contextEntry],
                isExactMatch: false,
                matchType: 'fuzzy',
                distance: distance
              });
            }
          }
        }
        
        return false; // We're just collecting in the Map, not directly in results
      });
      
    }
      
    // Combine word-order matches with other matches
    const allMatches = new Map([...wordOrderMatches, ...uniqueValues]);
    
    // Convert Map to array for display, using only unique values
    const deduplicatedResults = Array.from(allMatches.values())
      .map(item => ({
        value: item.value,
        originalSearch: item.originalSearch,
        isExactMatch: item.isExactMatch,
        matchType: item.matchType,
        // For display in UI, pick the first context
        tableId: item.contexts[0].tableId,
        columnName: item.contexts[0].columnName,
        tableName: item.contexts[0].tableName,
        distance: item.matchType === 'fuzzy' ? item.contexts[0].distance : 0,
        // But keep all contexts for when the item is selected
        valueContext: item.contexts
      }));
    
      if (shouldSortResults) {
        deduplicatedResults.sort((a, b) => {
          // First, prioritize by match type: word-order > exact > phonetic > fuzzy
          const matchTypePriority = { 'word-order': -1, 'exact': 0, 'phonetic': 1, 'fuzzy': 2 };
        const aPriority = matchTypePriority[a.matchType] || 999;
        const bPriority = matchTypePriority[b.matchType] || 999;
        
        if (aPriority !== bPriority) return aPriority - bPriority;
        
        // Within exact matches, prioritize starts-with matches
        if (a.matchType === 'exact' && b.matchType === 'exact') {
          const aStartsWithCurrent = a.value.toLowerCase().startsWith(currentWithoutL);
          const bStartsWithCurrent = b.value.toLowerCase().startsWith(currentWithoutL);
          if (aStartsWithCurrent !== bStartsWithCurrent) return bStartsWithCurrent ? 1 : -1;
        }
        
        // Within fuzzy matches, sort by distance (closer matches first)
        if (a.matchType === 'fuzzy' && b.matchType === 'fuzzy') {
          if (a.distance !== b.distance) return a.distance - b.distance;
        }
      
        // Then, if there are previous words, prioritize matches with the combined words
        if (previousWordLower) {
          const aMatchesCombined = a.value.toLowerCase().includes(combinedWordsLower);
          const bMatchesCombined = b.value.toLowerCase().includes(combinedWordsLower);
          if (aMatchesCombined !== bMatchesCombined) return bMatchesCombined ? 1 : -1;
        }
      
        // Finally, sort alphabetically
        return a.value.localeCompare(b.value);
      });
    }
  
    return { 
      results: deduplicatedResults.slice(0, maxResults),
      matchType: deduplicatedResults.length > 0 ? deduplicatedResults[0].matchType : 'none'
    };
  }, [minChars, maxResults, getIndexCache]);

  // Debounced version of searchValues
  const debouncedSearch = useCallback(
    debounce((searchTerm, options) => searchValues(searchTerm, options), debounceTime, { leading: true }), 
    [searchValues, debounceTime]
  );

  // Convenience function to get current word from text and cursor position
  const getCurrentWord = useCallback((text, position, suggestion = null) => {
    const beforeCursor = text.slice(0, position);
    const lastSpaceIndex = beforeCursor.lastIndexOf(' ');
    const lastWord = beforeCursor.slice(lastSpaceIndex + 1);
    
    // Get the previous word if it exists
    const textBeforeLastWord = beforeCursor.slice(0, lastSpaceIndex);
    const previousSpaceIndex = textBeforeLastWord.lastIndexOf(' ');
    const previousWord = lastSpaceIndex > 0 ? textBeforeLastWord.slice(previousSpaceIndex + 1) : '';
    
    const combinedWords = previousWord ? `${previousWord} ${lastWord}` : lastWord;
    
    if (suggestion) {
      const suggestionWords = suggestion.toLowerCase().split(' ');
      const textWords = beforeCursor.toLowerCase().split(' ');
      let matchStart = textWords.length - 1;
      
      for (let i = textWords.length - 1; i >= 0; i--) {
        const word = textWords[i];
        if (suggestionWords.some(sw => sw.startsWith(word))) {
          matchStart = i;
        } else {
          break;
        }
      }
      
      const startPos = textWords.slice(0, matchStart).join(' ').length;
      return {
        word: lastWord,
        previousWord,
        combinedWords,
        start: matchStart === 0 ? 0 : startPos + 1,
        end: beforeCursor.length,
        hasLPrefix: lastWord.startsWith('ל')
      };
    }
    
    return {
      word: lastWord,
      previousWord,
      combinedWords,
      start: lastSpaceIndex + 1,
      end: beforeCursor.length,
      hasLPrefix: lastWord.startsWith('ל')
    };
  }, []);

  // Cancel debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const clearCache = useCallback(() => {
    // Clear in-memory cache
    setIndexCache(null);
    
    // Clear localStorage items related to this cache
    try {
      // IMPORTANT: Explicitly clear the indexedColumns cache 
      const indexedColumnsKey = `${cachePrefix}_indexedColumns`;
      localStorage.removeItem(indexedColumnsKey);
      localStorage.removeItem(`${indexedColumnsKey}_expiry`);
      
      // Also clear other cache items
      const keys = Object.keys(localStorage);
      let clearedItems = 0;
      
      keys.forEach(key => {
        if (key.startsWith(`${cachePrefix}_`)) {
          localStorage.removeItem(key);
          clearedItems++;
        }
      });
      
    } catch (error) {
    }
  }, [cachePrefix]);
  
  return {
    loading,
    error,
    searchValues,
    debouncedSearch,
    fetchIndexedColumns,
    fetchColumnValues,
    getCurrentWord,
    getIndexCache,
    clearCache
  };
};

export default useIndexSearch;